import { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';

import postOrganizationTrialingSeats from '@cartwhl/common/api/postOrganizationTrialingSeats';
import * as checkoutStore from '@cartwhl/common/stores/checkoutStore';
import * as updatedProductStore from '@cartwhl/common/stores/updatedProductStore';
import type { UpdatedProductKey } from '@cartwhl/common/stores/updatedProductStore';
import type { OrganizationPlanType } from '@cartwhl/common/types';
import { getHomepageUrl } from '@cartwhl/common/utils';
import { getStripePortalSession } from '@cartwhl/common/utils/getStripePortalSession';
import { getStripeProductID } from '@cartwhl/common/utils/getStripeProductID';
import handleGenericErrors from '@cartwhl/common/utils/handleGenericErrors';
import {
  getStripeCustomerIdForBilling,
  isActiveFreeTrial,
} from '@cartwhl/common/utils/organizationWorkspace';
import Text from '@cartwhl/ui/base/Text';
import ArrowUpRightIcon from '@cartwhl/ui/icons/ArrowUpRightIcon';
import MinusIcon from '@cartwhl/ui/icons/MinusIcon';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import { noticesStore, userStore } from 'stores/global';

import { pricingTabConfigs } from '../../PricingPage/components/pricingPageData';

function planTypeToTeamProductKey(
  planType: OrganizationPlanType | undefined,
): UpdatedProductKey | null {
  if (
    planType === 'team_standard' ||
    planType === 'team_pro' ||
    planType === 'team_scale'
  ) {
    return planType;
  }
  return null;
}

function formatPerSeatMonthlyDisplay(
  unitAmount: number,
  interval: 'month' | 'year',
): string {
  const displayDollars = updatedProductStore.formatMoney(
    interval === 'year' ? unitAmount / 12 : unitAmount,
  );
  if (interval === 'year') {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    }).format(Math.round(displayDollars));
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: Number.isInteger(displayDollars) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(displayDollars);
}

export default function AddTeamSeatModal({ close }: { close: () => void }) {
  const { organizationInfo, userInfo } = useSnapshot(userStore.state);
  const { done: updatedProductsReady } = useSnapshot(updatedProductStore.state);

  const [quantity, setQuantity] = useState(1);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    void updatedProductStore.fetchProducts();
  }, []);

  function handleOpenChange(open: boolean) {
    if (!open) {
      close();
    }
  }

  const productKey = planTypeToTeamProductKey(organizationInfo?.planType);

  const teamCard = useMemo(() => {
    const cards = pricingTabConfigs['team-plans'].cards ?? [];
    if (!productKey) return undefined;
    return cards.find((c) => c.productKey === productKey);
  }, [productKey]);

  const billingInterval: 'month' | 'year' =
    organizationInfo?.billingCycle === 'yearly' ? 'year' : 'month';

  const stripeProductId = useMemo(() => {
    if (!productKey) return null;
    try {
      return getStripeProductID(productKey);
    } catch {
      return null;
    }
  }, [productKey]);

  const teamPrice =
    stripeProductId != null
      ? updatedProductStore.getPriceByProductAndMode(
          stripeProductId,
          billingInterval,
        )
      : null;

  const seatTypeName = teamCard?.title ?? 'Team seat';

  const priceAmount =
    teamPrice != null
      ? formatPerSeatMonthlyDisplay(teamPrice.unit_amount, billingInterval)
      : null;
  const priceLine = priceAmount != null ? `$${priceAmount} / mo / seat` : '—';

  const stripeCustomerId = getStripeCustomerIdForBilling(
    userInfo,
    organizationInfo,
  );

  const canCheckout =
    !!productKey &&
    !!teamPrice &&
    !!stripeCustomerId &&
    quantity > 0 &&
    updatedProductsReady;

  async function requestPortalSession() {
    if (!teamPrice || !stripeCustomerId) return;
    setRequesting(true);
    const seatsPurchased = organizationInfo?.seatsPurchased ?? 0;
    const newTotalQuantity = seatsPurchased + quantity;
    getStripePortalSession(stripeCustomerId, teamPrice.id, newTotalQuantity)
      .then(({ data }) => {
        if (typeof data !== 'undefined') {
          window.location.href = data.url;
        }
        setRequesting(false);
      })
      .catch(() => setRequesting(false));
  }

  const handleCheckout = async () => {
    if (!teamPrice || !stripeCustomerId) return;
    const seatsPurchased = organizationInfo?.seatsPurchased ?? 0;
    const orgId = organizationInfo?.organizationID;
    if (seatsPurchased === 0) {
      setRequesting(true);
      checkoutStore.select(teamPrice, quantity);
      await checkoutStore.startCheckout();
      close();
    } else if (isActiveFreeTrial(organizationInfo) && orgId) {
      setRequesting(true);
      const { error } = await postOrganizationTrialingSeats(
        orgId,
        seatsPurchased + quantity,
      );
      if (error) {
        await handleGenericErrors(error);
        setRequesting(false);
        return;
      }
      await userStore.fetchOrganization(orgId);
      await userStore.refetchUserInfo();
      await noticesStore.success(`${quantity} seats added successfully`);
      close();
    } else {
      requestPortalSession();
    }
  };

  return (
    <Modal
      title="Add team seats"
      maxWidth={684}
      open={true}
      onOpenChange={handleOpenChange}
    >
      <div className="flex flex-col gap-4">
        {!productKey ? (
          <Text as="p" variant="studio/body/md" className="text-tertiary">
            Adding seats for your current plan must be done through the billing
            portal or support.
          </Text>
        ) : null}

        <div className="flex items-center gap-2">
          <a
            className="ml-auto text-sm"
            href={getHomepageUrl('/pricing/team-plans')}
            target="_blank"
            rel="noreferrer"
          >
            <div className="flex items-center gap-2">
              <Text as="p" variant="studio/body/md">
                View team pricing
              </Text>
              <ArrowUpRightIcon />
            </div>
          </a>
        </div>

        <div className="border-blue bg-blue/10 flex flex-row items-center justify-between gap-4 rounded-lg border p-4">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Text as="h3" variant="studio/body/bold/md">
              {seatTypeName}
            </Text>
            <Text
              as="p"
              variant="studio/body/bold/md"
              className="flex flex-wrap items-baseline gap-x-1"
            >
              <span>{priceLine}</span>
              {billingInterval === 'year' ? (
                <Text
                  as="span"
                  variant="studio/body/sm"
                  className="text-tertiary font-normal"
                >
                  (billed yearly)
                </Text>
              ) : null}
            </Text>
            {teamCard ? (
              <Text as="p" variant="studio/body/md" className="text-tertiary">
                <span className="text-primary font-medium">
                  {teamCard.creditsValue}
                </span>{' '}
                {teamCard.creditsPeriodLabel}.
              </Text>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center justify-end gap-2 self-center rounded-lg p-2">
            <Button
              className="dark:bg-gray-90 h-8 w-8 bg-white"
              icon={<MinusIcon />}
              variant="secondary"
              size="sm"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={quantity <= 1}
              aria-label="Decrease quantity"
            />
            <Text as="span" variant="studio/body/bold/md">
              {quantity}
            </Text>
            <Button
              className="dark:bg-gray-90 bg-white"
              icon={<PlusIcon />}
              variant="secondary"
              size="sm"
              onClick={() => setQuantity(quantity + 1)}
              aria-label="Increase quantity"
            />
          </div>
        </div>

        <Text as="p" variant="studio/body/md">
          Additional seats will be billed to your account at the conclusion of
          the trial period.
        </Text>

        <div className="mt-2 flex flex-row flex-nowrap">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            className="ml-auto"
            variant="primary"
            loading={requesting}
            onClick={handleCheckout}
            disabled={!canCheckout}
          >
            Add seats
          </Button>
        </div>
      </div>
    </Modal>
  );
}
